import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MarkdownEditor from './MarkdownEditor';
import { BlogPost } from '../types';
import { fetchDocumentByID, updateDocument, createDocument } from '../services/api';

const BlogPostEditor: React.FC<{ existing: boolean }> = ({ existing }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [post, setPost] = useState<BlogPost>({
    id: 'new-post',
    content: '',
    subtitle: '',
    author_username: 'current-user',
    posted_at: '',
    title: 'Untitled',
    status: 'draft',
  });
  const [id, setId] = useState<string | null>(null);
  const [title, setTitle] = useState('Untitled');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!existing) {
      setId(null);
      return;
    }
    const pathParts = location.pathname.split('/');
    if (pathParts.length === 3 && pathParts[1] === 'editor' && pathParts[2]) {
      setId(pathParts[2]);
    }
  }, [location]);

  // Load existing post
  useEffect(() => {
    const loadPost = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const document = await fetchDocumentByID(id);

        if (document) {
          setPost(document);
          setTitle(document.title || 'Untitled');
        } else {
          console.error('Failed to load blog post');
          setError('Failed to load blog post');
        }
      } catch (err) {
        setError('Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [id]);

  const handleContentChange = (content: string) => {
    setPost(prev => ({ ...prev, content }));
  };

  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
    setPost(prev => ({ ...prev, title: newTitle }));
  };

  const handleSubtitleChange = (newSubtitle: string) => {
    setPost(prev => ({ ...prev, subtitle: newSubtitle }));
  };

  const handleStatusChange = (newStatus: string) => {
    setPost(prev => {
      const updatedPost = { ...prev };

      // Set the explicit status
      updatedPost.status = newStatus as 'draft' | 'published' | 'archived' | 'retracted';

      // Handle posted_at based on status
      if (newStatus === 'published' && !prev.posted_at) {
        // If changing to published and no posted_at, set current date
        updatedPost.posted_at = new Date().toISOString();
      } else if (newStatus === 'draft') {
        // If changing to draft, remove posted_at
        updatedPost.posted_at = '';
      }
      // For archived and retracted, keep posted_at if it exists

      return updatedPost;
    });
  };

  const pushDocumentUpdate = async (updatedPost: BlogPost) => {
    if (existing && id) {
      await updateDocument(updatedPost);
    } else {
      await createDocument(updatedPost);
    }
  }

  const handlePublish = async () => {
    try {
      await pushDocumentUpdate({ ...post, status: 'published' });
      navigate('/profile');
    } catch (err) {
      setError('Failed to publish document');
    }
  };

  const handleSaveDraft = async () => {
    try {
      await pushDocumentUpdate({ ...post, status: 'draft' });
      navigate('/profile');
    } catch (err) {
      setError('Failed to save draft');
    }
  };

  const handleCancel = () => {
    navigate('/profile');
  };

  if (loading) {
    return (
      <div className="blog-post-editor">
        <div className="loading-state">Loading post...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="blog-post-editor">
        <div className="error-state">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/profile')}>Back to Profile</button>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-post-editor">
      <main className="editor-main">
        <MarkdownEditor
          onContentChange={handleContentChange}
          placeholder="Tell your story..."
          initialContent={post.content}
          title={title}
          onTitleChange={handleTitleChange}
          onSubtitleChange={handleSubtitleChange}
          onStatusChange={handleStatusChange}
          post={post}
          key={post.id} // Force re-render when editing different posts
        />
      </main>

      <footer className="editor-footer">
        <div className="footer-left">
          <span className="word-count">
            {post.content.length} characters
          </span>
        </div>
        <div className="footer-right">
          <button
            className="btn btn-tertiary"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleSaveDraft}
          >
            Save Draft
          </button>
          <button
            className="btn btn-primary"
            onClick={handlePublish}
          >
            Publish
          </button>
        </div>
      </footer>
    </div>
  );
};

export default BlogPostEditor;